/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  CategoricalScheme,
  ColorScheme,
  ColorSchemeConfig,
  getCategoricalSchemeRegistry,
  getSequentialSchemeRegistry,
  SequentialScheme,
  SequentialSchemeConfig,
  CategoricalAirbnb,
  CategoricalD3,
  CategoricalEcharts,
  CategoricalGoogle,
  CategoricalLyft,
  CategoricalPreset,
  CategoricalSuperset,
  SequentialCommon,
  SequentialD3,
  ColorSchemeRegistry,
} from '@superset-ui/core';

const SEQUENTIAL_SCHEME_LABEL_MAP: Record<string, string> = {
  blue_white_yellow: '蓝/白/黄',
  fire: '火焰',
  white_black: '白/黑',
  black_white: '黑/白',
  dark_blue: '深蓝渐变',
  pink_grey: '粉/灰',
  greens: '绿色渐变',
  purples: '紫色渐变',
  oranges: '橙色渐变',
  red_yellow_blue: '红/黄/蓝',
  brown_white_green: '棕/白/绿',
  purple_white_green: '紫/白/绿',
  superset_seq_1: 'Superset 顺序 #1',
  superset_seq_2: 'Superset 顺序 #2',
  superset_div_1: 'Superset 发散 #1',
  superset_div_2: 'Superset 发散 #2',
  preset_seq_1: 'Preset 顺序 #1',
  preset_seq_2: 'Preset 顺序 #2',
  preset_div_1: 'Preset 发散 #1',
  preset_div_2: 'Preset 发散 #2',
  echarts_gradient: 'ECharts 渐变',
  deck_gl_heatmap_gradient: 'Deck.gl 热力图默认',
  schemeRdBu: '红/蓝',
  schemeBrBG: '棕/绿',
  schemePRGn: '紫/绿',
  schemePiYG: '粉/绿',
  schemePuOr: '紫/橙',
  schemeRdGy: '红/灰/黑',
  schemeRdYlBu: '红/黄/蓝',
  schemeRdYlGn: '红/黄/绿',
  schemeSpectral: '彩虹',
  schemeBlues: '蓝色渐变',
  schemeGreens: '绿色渐变',
  schemeGrays: '灰色渐变',
  schemeOranges: '橙色渐变',
  schemePurples: '紫色渐变',
  schemeReds: '红色渐变',
  schemeViridis: 'Viridis（绿紫）',
  schemeInferno: 'Inferno（熔岩）',
  schemeMagma: 'Magma（岩浆）',
  schemeWarm: 'Warm（暖色）',
  schemeCool: 'Cool（冷色）',
  schemeCubehelixDefault: 'Cube Helix（立方螺旋）',
  schemeBuGn: '蓝/绿',
  schemeBuPu: '蓝/紫',
  schemeGnBu: '绿/蓝',
  schemeOrRd: '橙/红',
  schemePuBuGn: '紫/蓝/绿',
  schemePuBu: '紫/蓝',
  schemePuRd: '紫/红',
  schemeRdPu: '红/紫',
  schemeYlGnBu: '黄/绿/蓝',
  schemeYlGn: '黄/绿',
  schemeYlOrBr: '黄/橙/棕',
  schemeYlOrRd: '黄/橙/红',
};

function localizeSequentialSchemeLabel(scheme: SequentialScheme) {
  const localizedLabel = SEQUENTIAL_SCHEME_LABEL_MAP[scheme.id];

  if (!localizedLabel || localizedLabel === scheme.label) {
    return scheme;
  }

  return new SequentialScheme({
    id: scheme.id,
    label: localizedLabel,
    colors: scheme.colors,
    description: scheme.description,
    isDefault: scheme.isDefault,
    isDiverging: scheme.isDiverging,
  });
}

function registerColorSchemes<T extends ColorScheme>(
  registry: ColorSchemeRegistry<T>,
  colorSchemes: T[],
  standardDefaultKey: string,
) {
  colorSchemes.forEach(scheme => {
    registry.registerValue(scheme.id, scheme);
  });

  const defaultKey =
    colorSchemes.find(scheme => scheme.isDefault)?.id || standardDefaultKey;
  registry.setDefaultKey(defaultKey);
}

export default function setupColors(
  extraCategoricalColorSchemeConfigs: ColorSchemeConfig[] = [],
  extraSequentialColorSchemeConfigs: SequentialSchemeConfig[] = [],
) {
  const extraCategoricalColorSchemes = extraCategoricalColorSchemeConfigs.map(
    config => new CategoricalScheme(config),
  );
  const extraSequentialColorSchemes = extraSequentialColorSchemeConfigs.map(
    config => new SequentialScheme(config),
  );
  registerColorSchemes(
    // @ts-ignore
    getCategoricalSchemeRegistry(),
    [
      ...CategoricalAirbnb,
      ...CategoricalD3,
      ...CategoricalEcharts,
      ...CategoricalGoogle,
      ...CategoricalLyft,
      ...CategoricalPreset,
      ...CategoricalSuperset,
      ...extraCategoricalColorSchemes,
    ],
    'supersetColors',
  );
  registerColorSchemes(
    // @ts-ignore
    getSequentialSchemeRegistry(),
    [...SequentialCommon, ...SequentialD3, ...extraSequentialColorSchemes].map(
      localizeSequentialSchemeLabel,
    ),
    'purples',
  );
}
